import { CardPrimitive } from '@figma/fpl-components';
import { color as token, spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import cssVars from '@figma/fpl-tokens/css-vars';
import { Box } from '@/UI/Box/Box';
import { CardContent } from '@/UI/Card/Card';
import { color, EL_COLORS } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { I18N_KEYS } from '@/language/keys';
import { FlexCenHorVer, FlexCenVer, FlexCol } from '@/UI/styles';
import { FolderEmptyIcon } from '@/UI/Icons/FolderEmptyIcon';
import { TextSmRg, TextStdRg } from '@/UI/AppTypography/AppTypography';
import { useIsHovered } from '@/hooks/useIsHovered';
import { FolderLargeEmptyIcon } from '@/UI/Icons/FolderLargeEmptyIcon';
import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@/UI/theme.exports';

const styles = stylex.create({
  // Name-row wrapper: replaces the flag-off MUI CardContent (which FPL does not provide). Wrapped in
  // CardPrimitive.Interactive by the caller path so its content stays clickable inside an interactive card.
  content: {
    paddingTop: spacer.spacer8px,
    paddingBottom: spacer.spacer8px,
    paddingLeft: spacer.spacer8px,
    // Match legacy CardContent's default 16px right padding (only pt/pb/pl were overridden to 8px).
    paddingRight: spacer.spacer16px,
  },
});

interface FolderCardDisplayProps {
  id: string;
  name: string;
  posters: string[];
  totalFiles: number;
  fileCount?: number;
  /** What the count names. Workflows pages count workflows; the mixed list still counts files. */
  countKind?: 'file' | 'workflow';
  subtitle?: ReactNode;
  nameContent?: ReactNode;
  hoverOverlay?: ReactNode;
  forceShowOverlay?: boolean;
  thumbnailSx?: SxProps<Theme>;
  /**
   * FPL branch only: hover state supplied by the parent. Under `CardPrimitive.Root` the thumbnail grid
   * is a `pointer-events: none` descendant, so its own mouse handlers never fire. When omitted (e.g.
   * ArchiveFolderCard's plain container) the component self-manages hover.
   */
  isHovered?: boolean;
}

export function FolderCardDisplay({
  id,
  name,
  posters,
  totalFiles,
  fileCount,
  countKind = 'file',
  subtitle,
  nameContent,
  hoverOverlay,
  forceShowOverlay,
  thumbnailSx,
  isHovered: isHoveredProp,
}: FolderCardDisplayProps) {
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();
  const { isHovered: selfHovered, ...hoveredProps } = useIsHovered();
  const isControlled = isHoveredProp !== undefined;
  const isHovered = isControlled ? isHoveredProp : selfHovered;
  const extraCount = totalFiles > 4 ? totalFiles - 4 : 0;
  const thumbnailRadius = isFplMigrationEnabled ? cssVars.radius.large : 2;
  const count = fileCount ?? totalFiles;
  const countLabelKey =
    countKind === 'workflow'
      ? count === 1
        ? I18N_KEYS.MAIN_DASHBOARD.FOLDER_CARD.WORKFLOW_COUNT_SINGULAR
        : I18N_KEYS.MAIN_DASHBOARD.FOLDER_CARD.WORKFLOW_COUNT_PLURAL
      : count === 1
        ? I18N_KEYS.MAIN_DASHBOARD.FOLDER_CARD.FILE_COUNT_SINGULAR
        : I18N_KEYS.MAIN_DASHBOARD.FOLDER_CARD.FILE_COUNT_PLURAL;
  const countLabel = t(countLabelKey, { count });

  const nameRow = (
    <FlexCenVer sx={{ gap: 1 }}>
      <FolderEmptyIcon width={24} height={24} color={token.textSecondary} />
      <FlexCol sx={{ width: '100%' }}>
        {nameContent ?? <TextStdRg noWrap text={name} color={token.text} />}
        {subtitle ?? <TextSmRg noWrap text={countLabel} color={token.textSecondary} />}
      </FlexCol>
    </FlexCenVer>
  );

  return (
    <>
      <Box
        data-testid={`body-folder-card-${id}`}
        {...(isControlled ? {} : hoveredProps)}
        sx={[
          {
            width: '100%',
            height: 240,
            bgcolor: isFplMigrationEnabled ? cssVars.color.bg : color.Black92,
            borderRadius: thumbnailRadius,
            position: 'relative',
            border: '1px solid',
            borderColor: isFplMigrationEnabled ? token.border : color.White08_T,
            overflow: 'hidden',
          },
          ...(Array.isArray(thumbnailSx) ? thumbnailSx : thumbnailSx ? [thumbnailSx] : []),
        ]}
      >
        {posters.length > 0 ? (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gridTemplateRows: 'repeat(2, 1fr)',
                gap: 1.5,
                padding: 2,
                height: '100%',
                width: '100%',
                position: 'relative',
              }}
            >
              {[0, 1, 2, 3].map((index) => {
                const image = posters[index];
                const isLastSlot = index === 3;
                const shouldShowOverlay = isLastSlot && extraCount > 0;

                if (!image) {
                  return <Box key={index} />;
                }

                if (image === 'folder') {
                  return (
                    <FlexCenHorVer
                      key={index}
                      sx={{
                        borderRadius: thumbnailRadius,
                        bgcolor: isFplMigrationEnabled ? cssVars.color.bg : color.Black92,
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                        border: `1px solid ${EL_COLORS.DividerBorder}`,
                      }}
                    >
                      <Box sx={{ opacity: shouldShowOverlay ? 0.5 : 1 }}>
                        <FolderEmptyIcon width={32} height={32} color={token.text} />
                      </Box>
                      {shouldShowOverlay && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            borderRadius: thumbnailRadius,
                            bgcolor: color.Black64_T,
                          }}
                        />
                      )}
                    </FlexCenHorVer>
                  );
                }

                return (
                  <Box
                    key={index}
                    sx={{
                      borderRadius: thumbnailRadius,
                      overflow: 'hidden',
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      border: `1px solid ${EL_COLORS.DividerBorder}`,
                    }}
                  >
                    <img
                      src={image}
                      alt="workflow poster"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center',
                      }}
                    />
                    {shouldShowOverlay && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          borderRadius: thumbnailRadius,
                          backgroundImage: `linear-gradient(90deg, ${color.Black64_T} 0%, ${color.Black64_T} 100%)`,
                        }}
                      />
                    )}
                  </Box>
                );
              })}
              {extraCount > 0 && (
                <FlexCenHorVer
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    color: token.text,
                    height: 'calc(50% + 10px)',
                    width: 'calc(50% + 12px)',
                  }}
                >
                  +{extraCount}
                </FlexCenHorVer>
              )}
            </Box>
            {isHovered && !hoverOverlay && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: token.bgSecondary,
                  pointerEvents: 'none',
                }}
              />
            )}
            {(isHovered || forceShowOverlay) && hoverOverlay}
          </>
        ) : (
          <>
            <FlexCenHorVer sx={{ width: '100%', height: '100%' }}>
              <FolderLargeEmptyIcon width={42} height={42} color={token.text} />
            </FlexCenHorVer>
            {isHovered && !hoverOverlay && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: token.bgSecondary,
                  pointerEvents: 'none',
                }}
              />
            )}
            {(isHovered || forceShowOverlay) && hoverOverlay}
          </>
        )}
      </Box>
      {isFplMigrationEnabled === true ? (
        <CardPrimitive.Interactive {...stylex.props(styles.content)}>{nameRow}</CardPrimitive.Interactive>
      ) : (
        <CardContent
          sx={{
            pt: 1,
            pb: 1,
            pl: 1,
            backgroundColor: 'transparent',
            boxShadow: 'none',
            backgroundImage: 'initial',
          }}
        >
          {nameRow}
        </CardContent>
      )}
    </>
  );
}
